import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Permission, CargoFranquia } from '../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: Permission;
  role?: CargoFranquia;
  requireTact?: boolean;
  requireFranquia?: boolean;
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  role,
  requireTact = false,
  requireFranquia = false,
  fallbackPath = '/unauthorized'
}) => {
  const { user, isAuthenticated, isLoading, hasPermission, hasRole, isTactUser, isFranquiaUser } = useAuth();

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#29306a] to-[#32bef0]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  // Redirecionar para login se não autenticado
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Verificar se requer usuário TACT
  if (requireTact && !isTactUser()) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar se requer usuário de franquia
  if (requireFranquia && !isFranquiaUser()) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar permissão específica
  if (permission && !hasPermission(permission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar cargo específico
  if (role && !hasRole(role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Se passou em todas as verificações, renderizar o componente
  return <>{children}</>;
};

// Componente para mostrar conteúdo baseado em permissões
interface ConditionalRenderProps {
  children: React.ReactNode;
  permission?: Permission;
  role?: CargoFranquia;
  requireTact?: boolean;
  requireFranquia?: boolean;
  fallback?: React.ReactNode;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  permission,
  role,
  requireTact = false,
  requireFranquia = false,
  fallback = null
}) => {
  const { user, hasPermission, hasRole, isTactUser, isFranquiaUser } = useAuth();

  if (!user) {
    return <>{fallback}</>;
  }

  // Verificar se requer usuário TACT
  if (requireTact && !isTactUser()) {
    return <>{fallback}</>;
  }

  // Verificar se requer usuário de franquia
  if (requireFranquia && !isFranquiaUser()) {
    return <>{fallback}</>;
  }

  // Verificar permissão específica
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Verificar cargo específico
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  // Se passou em todas as verificações, renderizar o conteúdo
  return <>{children}</>;
};

// Hook para verificar múltiplas condições
export const usePermissions = () => {
  const { user, hasPermission, hasRole, isTactUser, isFranquiaUser } = useAuth();

  const checkMultiplePermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const checkAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const checkMultipleRoles = (roles: CargoFranquia[]): boolean => {
    return roles.every(role => hasRole(role));
  };

  const checkAnyRole = (roles: CargoFranquia[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  return {
    user,
    hasPermission,
    hasRole,
    isTactUser,
    isFranquiaUser,
    checkMultiplePermissions,
    checkAnyPermission,
    checkMultipleRoles,
    checkAnyRole
  };
};
